import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';
import type { ResponseObject } from '@/types/shared';

/**
 * Internal ratings — the signed-in employee's received ratings and the
 * tickets awaiting their rating. Mirrors
 * `datacentral-client-v4/src/modules/InternalRating/services/index.ts`.
 *
 * Note: the backend uses a "load-more" envelope (`items` + `has_more`)
 * instead of the standard `results` + `Pagination` envelope. We expose the
 * same shape so callers can detect the last page via `has_more === false`.
 */

/* ── Types ─────────────────────────────────────────────────────────────── */

export interface InternalRating {
  rating_id: number;
  entity_type: string;
  entity_id: number;
  rater_employee_id: number;
  /** Name of the rater — present only on my-received rows. */
  rater_employee_name?: string;
  rater_employee_code?: string;
  rater_department_name?: string;
  entity_title?: string;
  entity_type_label?: string;
  /** Names of all employees rated in the same batch (includes the signed-in user). */
  rated_employee_names?: string[];
  rated_employee_ids: number[];
  rated_departments: number[];
  rating: number;
  comment: string;
  created_at: string;
}

export interface RatingStarBucket {
  star: number;
  count: number;
}

export interface RatingSummary {
  total_ratings: number;
  average_rating: number;
  /** Star → count breakdown (1..5). Computed over ALL ratings, not the current page. */
  distribution: RatingStarBucket[];
}

/** `my-received` response: load-more list + overall summary. */
export interface PagedRatings {
  items: InternalRating[];
  has_more: boolean;
  summary?: RatingSummary;
}

export interface AwaitingRatingItem {
  ticket_id: number;
  ticket_content: string;
  ticket_type: string;
  completed_at: string;
  rated_department_names: string[];
  rated_employee_names?: string[];
  rating_due_at?: string | null;
  reminder_count?: number;
}

/** `awaiting` response: load-more list. */
export interface PagedAwaitingRatings {
  items: AwaitingRatingItem[];
  has_more: boolean;
}

export interface ReceivedRatingFilters {
  /** 1..5 — maps to ROUND(rating) buckets. */
  star?: number;
  /** YYYY-MM-DD lower bound (inclusive). */
  from_date?: string;
  /** YYYY-MM-DD upper bound (inclusive). */
  to_date?: string;
  /** Matches rater name or employee code. */
  rater_search?: string;
  /** true = commented only, false = uncommented only. */
  with_comment?: boolean;
}

/* ── Query key factory ──────────────────────────────────────────────────── */

export const ratingKeys = {
  myReceived: (page: number, pageSize: number, filters: ReceivedRatingFilters) =>
    ['ratings', 'my-received', page, pageSize, filters] as const,
  awaiting: (page: number, pageSize: number) =>
    ['ratings', 'awaiting', page, pageSize] as const,
};

/* ── Helpers ────────────────────────────────────────────────────────────── */

function buildQuery(params: Record<string, unknown>): string {
  return Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
}

/* ── Hooks ──────────────────────────────────────────────────────────────── */

/**
 * `GET /ratings/my-received?page=&page_size=&star=&from_date=&to_date=&rater_search=&with_comment=`
 *
 * Returns the signed-in employee's received ratings, paginated with
 * `has_more` (not the standard pagination envelope). Summary is always
 * computed over the full dataset regardless of the active filter.
 */
export function useMyReceivedRatings(
  page: number,
  pageSize: number,
  filters: ReceivedRatingFilters = {},
) {
  return useQuery({
    queryKey: ratingKeys.myReceived(page, pageSize, filters),
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<PagedRatings> => {
      const res = await dataCentralApi.get<ResponseObject<PagedRatings>>(
        `/ratings/my-received?${buildQuery({ page, page_size: pageSize, ...filters })}`,
      );
      return res.data.result;
    },
  });
}

/**
 * `GET /ratings/awaiting?page=&page_size=`
 *
 * Tickets that the signed-in employee has created and can rate (terminal
 * service tickets, not yet rated by them).
 */
export function useAwaitingRatings(page: number, pageSize: number) {
  return useQuery({
    queryKey: ratingKeys.awaiting(page, pageSize),
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<PagedAwaitingRatings> => {
      const res = await dataCentralApi.get<ResponseObject<PagedAwaitingRatings>>(
        `/ratings/awaiting?${buildQuery({ page, page_size: pageSize })}`,
      );
      return res.data.result;
    },
  });
}
